import clsx from 'clsx';
import { Form, Formik } from 'formik';
import moment, { Moment } from 'moment';
import React from 'react';

import Button from '@components/Button';
import IconCheckmark from '@components/Images/IconCheckmark';
import CustomSpinner from '@components/Spinner/CustomSpinner';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import useCurrentUserUpdate from '@hooks/useCurrentUserUpdate';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { OfferOption, SelectedExtendedOffer } from '@utils/backend-api/subscribe';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/subscriptionCardOptions';

import Tag from './Tag';
import validationDobSchema from './validationDobSchema';

interface OfferProps {
  isSingleOffer?: boolean;
  offer: OfferOption;
  onOfferSelect: () => void;
  selectedOfferCode?: string;
  setSelectedOffer?: (plan: SelectedExtendedOffer) => void;
}

interface FormValues {
  dob: Moment;
}

const Offer = ({
  offer: {
    billingFrequency,
    billingFrequencyOverride,
    code,
    currentPrice,
    currentPriceDisplayOverride,
    description,
    isPro,
    isBestValue,
    listOfBenefits,
    originalPrice,
    originalPriceDisplayOverride,
    title,
  },
  onOfferSelect,
  selectedOfferCode,
  setSelectedOffer,
}: OfferProps) => {
  const { user, setUser } = useSubscribeForm();
  const { dob: initialDob } = user;
  const { updateCurrentUserAsync, isLoadingUpdate } = useCurrentUserUpdate();
  const isSelected = selectedOfferCode === code;
  const currentPriceToDisplay = currentPriceDisplayOverride || currentPrice;
  const hasDiscount = originalPrice !== currentPrice;
  const originalPriceToDisplay = originalPriceDisplayOverride || originalPrice;
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);

  const handleOfferSubmit = async ({ dob }: FormValues) => {
    if (isYPPlan && !initialDob) {
      const formattedDob = dob.format('YYYY-MM-DD');
      await updateCurrentUserAsync({ dob: formattedDob });
      setUser({ ...user, dob: formattedDob });
    }
    // Navigate to checkout page
    onOfferSelect();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        dob: initialDob ? moment(initialDob) : null,
      }}
      onSubmit={handleOfferSubmit}
      validationSchema={validationDobSchema(isYPPlan)}
    >
      {({ errors, touched, values, setFieldValue }) => (
        <Form>
          <div
            id={code}
            className={clsx(
              'relative flex flex-col overflow-hidden rounded-10 border border-solid border-transparent px-16 py-[44px] sm:h-full md:py-36',
              {
                'selected !border-primary-black': isSelected,
              }
            )}
          >
            {isBestValue && (
              <Tag
                name="Best Value"
                className="best-value absolute left-0 right-0 top-0 !rounded-0 text-center"
              />
            )}
            <h2 className="m-0 min-h-[48px] text-center font-serif text-headline-xs font-bold text-black">
              {title}
            </h2>
            <div className="flex flex-col items-center justify-center gap-8">
              <h3 className="m-0 min-h-[20px] font-sans text-subtitle-serif text-grayscale-60 line-through">
                {hasDiscount && `$${originalPriceToDisplay.toFixed(2)}`}
              </h3>
              <h3 className="m-0 font-serif text-headline-xs font-bold text-black">
                ${currentPriceToDisplay.toFixed(2)}
                {billingFrequencyOverride && (
                  <span className="ml-8 font-sans text-body-sm font-normal text-grayscale-60">
                    {billingFrequencyOverride}
                  </span>
                )}
              </h3>
              <span className="font-sans text-[13px] font-medium text-grayscale-60 sm:min-h-[20px]">
                {isYPPlan && '30 years or under'}
              </span>
            </div>
            {isYPPlan && isSelected && (
              <div className="mt-16 w-full">
                <DesktopDatePicker
                  disableFuture
                  disabled={!!initialDob}
                  maxDate={moment().subtract(13, 'years')}
                  minDate={moment().subtract(31, 'years')}
                  onChange={(value) => setFieldValue('dob', value, true)}
                  value={values.dob}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      placeholder: 'Birthday',
                      size: 'small',
                      name: 'dob',
                      className: touched.dob && errors?.dob ? 'is-invalid' : '',
                    },
                  }}
                />
                {touched.dob && errors?.dob && (
                  <div className="mt-6 text-body-sm text-primary-red">
                    {errors.dob as unknown as string}
                  </div>
                )}
              </div>
            )}
            <Button
              type="submit"
              onClick={() =>
                setSelectedOffer({
                  billingFrequency,
                  code,
                  currentPrice,
                  description,
                  isPro,
                  isBestValue,
                  originalPrice,
                  title,
                })
              }
              className={clsx('mt-16 w-full rounded-2 !capitalize', {
                '!border-pro-red !bg-pro-red': isPro,
              })}
            >
              {isLoadingUpdate ? (
                <CustomSpinner
                  color="#fff"
                  size={10}
                  style={{ display: 'flex', justifyContent: 'center' }}
                />
              ) : (
                'Subscribe'
              )}
            </Button>
            <div className="my-16 md:my-24">
              {listOfBenefits.length > 0 &&
                listOfBenefits.map((benefit, index) => (
                  <p
                    key={index}
                    className={clsx(
                      'mb-16 mt-0 flex items-start font-sans text-body-sm text-grayscale-60 last:mb-0',
                      {
                        'font-bold': isPro,
                        'font-medium': !isPro,
                      }
                    )}
                  >
                    <IconCheckmark
                      className={clsx('mr-8 mt-4 w-10 shrink-0', {
                        'text-primary-black': !isPro,
                        'text-pro-red': isPro,
                      })}
                    />
                    <span>{benefit}</span>
                  </p>
                ))}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Offer;
